// Pipe specification schema
// Defines physical properties of a pipe type

import { z } from 'zod';
import { ValueWithUnitSchema } from '../base/common';

/**
 * Pipe Size Option Schema
 * Defines a specific size option within a pipe specification
 * 
 * Each size option represents a discrete pipe diameter available for the specification
 * (e.g., 100mm, 150mm, 200mm). Pipe edges reference these size options by ID
 * to avoid duplicating diameter data.
 */
export const PipeSizeOptionSchema = z.object({
  id: z.string(), // Unique identifier for this size option (e.g., "size_100mm")
  name: z.string(), // Display name (e.g., "100mm", "4 inch")
  inner_diameter: ValueWithUnitSchema, // Inner diameter with unit, e.g., { value: 100, unit: "mm" }
});

/**
 * Pipe Specification Schema
 * Defines the physical properties of a pipe type
 * 
 * Pipe specifications are normalized at the document level and referenced by ID
 * from edges (ADR-0003: Graph-Based Structure with ID References)
 */
export const PipeSpecificationSchema = z.object({
  id: z.string(),
  name: z.string(),

  // Calculation data
  material: z.string(),
  metric: z.boolean(),
  roughness: ValueWithUnitSchema,
  schedule: z.string(),
  standard: z.string(),
  standard_version: z.string(),

  // Size options
  sizes: z.array(PipeSizeOptionSchema),

  // v2 additions
  note: z.string().optional(),
  modulus_of_elasticity: ValueWithUnitSchema.optional(),
  thermal_conductivity: ValueWithUnitSchema.optional(),
  /** Hazen-Williams roughness coefficient C — dimensionless, higher = smoother */
  hazen_williams_C: z.number().optional(),
  percent_degradation: z.number().optional(),
});

export type PipeSizeOption = z.infer<typeof PipeSizeOptionSchema>;
export type PipeSpecification = z.infer<typeof PipeSpecificationSchema>;

